import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import Header from "@/components/layout/header";
import { DsShowcase } from "@/components/ds/DsShowcase";

export default function DesignSystem() {
  return (
    <div className="flex-1 space-y-6 p-4 md:p-6 lg:p-8">
      <Header
        title="Design System"
        description="Component library, design tokens, and styling guidelines for the Athlyn CMS Portal"
      />

      <Tabs defaultValue="colors" className="w-full">
        <TabsList className="grid w-full grid-cols-5 lg:w-auto">
          <TabsTrigger value="colors">Colors</TabsTrigger>
          <TabsTrigger value="typography">Typography</TabsTrigger>
          <TabsTrigger value="spacing">Spacing</TabsTrigger>
          <TabsTrigger value="components">Components</TabsTrigger>
          <TabsTrigger value="custom">Custom Classes</TabsTrigger>
        </TabsList>

        {/* Colors Tab */}
        <TabsContent value="colors" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Brand Colors</CardTitle>
              <CardDescription>Primary brand colors for Athlyn</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ColorSwatch
                  name="Athlyn Teal"
                  variable="--athlyn-teal"
                  value="hsl(186, 100%, 37%)"
                  className="bg-[hsl(186,100%,37%)]"
                />
                <ColorSwatch
                  name="Athlyn Blue"
                  variable="--athlyn-blue"
                  value="hsl(195, 100%, 39%)"
                  className="bg-[hsl(195,100%,39%)]"
                />
                <ColorSwatch
                  name="Athlyn Blue Light"
                  variable="--athlyn-blue-light"
                  value="hsl(195, 100%, 95%)"
                  className="bg-[hsl(195,100%,95%)]"
                  textDark
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Semantic Colors</CardTitle>
              <CardDescription>Contextual colors for UI elements</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ColorSwatch
                  name="Primary"
                  variable="--primary"
                  value="hsl(195, 100%, 39%)"
                  className="bg-primary"
                />
                <ColorSwatch
                  name="Secondary"
                  variable="--secondary"
                  className="bg-secondary"
                  textDark
                />
                <ColorSwatch
                  name="Accent"
                  variable="--accent"
                  className="bg-accent"
                  textDark
                />
                <ColorSwatch
                  name="Destructive"
                  variable="--destructive"
                  value="hsl(0, 84%, 60%)"
                  className="bg-destructive"
                />
                <ColorSwatch
                  name="Muted"
                  variable="--muted"
                  className="bg-muted"
                  textDark
                />
                <ColorSwatch
                  name="Background"
                  variable="--background"
                  className="bg-background border"
                  textDark
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Gray Scale</CardTitle>
              <CardDescription>Athlyn gray palette (50-900)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                <ColorSwatch
                  name="Gray 50"
                  variable="--athlyn-gray-50"
                  value="hsl(210, 40%, 98%)"
                  className="bg-[hsl(210,40%,98%)]"
                  textDark
                />
                <ColorSwatch
                  name="Gray 100"
                  variable="--athlyn-gray-100"
                  value="hsl(210, 40%, 96%)"
                  className="bg-[hsl(210,40%,96%)]"
                  textDark
                />
                <ColorSwatch
                  name="Gray 200"
                  variable="--athlyn-gray-200"
                  value="hsl(210, 16%, 93%)"
                  className="bg-[hsl(210,16%,93%)]"
                  textDark
                />
                <ColorSwatch
                  name="Gray 300"
                  variable="--athlyn-gray-300"
                  value="hsl(210, 14%, 83%)"
                  className="bg-[hsl(210,14%,83%)]"
                  textDark
                />
                <ColorSwatch
                  name="Gray 500"
                  variable="--athlyn-gray-500"
                  value="hsl(210, 9%, 31%)"
                  className="bg-[hsl(210,9%,31%)]"
                />
                <ColorSwatch
                  name="Gray 600"
                  variable="--athlyn-gray-600"
                  value="hsl(210, 10%, 23%)"
                  className="bg-[hsl(210,10%,23%)]"
                />
                <ColorSwatch
                  name="Gray 900"
                  variable="--athlyn-gray-900"
                  value="hsl(210, 24%, 16%)"
                  className="bg-[hsl(210,24%,16%)]"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Typography Tab */}
        <TabsContent value="typography" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Font Family</CardTitle>
              <CardDescription>Inter font is used throughout the application</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="font-sans text-2xl">Inter Sans-Serif</p>
                <code className="text-sm text-muted-foreground">font-family: 'Inter', sans-serif</code>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Type Scale</CardTitle>
              <CardDescription>Standard text sizes and their usage</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <TypeExample size="text-xs" label="Extra Small (0.75rem)" usage="Labels, badges, timestamps">
                The quick brown fox jumps over the lazy dog
              </TypeExample>
              <Separator />
              <TypeExample size="text-sm" label="Small (0.875rem)" usage="Body text, form inputs (most common)">
                The quick brown fox jumps over the lazy dog
              </TypeExample>
              <Separator />
              <TypeExample size="text-base" label="Base (1rem)" usage="Default body text">
                The quick brown fox jumps over the lazy dog
              </TypeExample>
              <Separator />
              <TypeExample size="text-lg" label="Large (1.125rem)" usage="Subtitles, section headers">
                The quick brown fox jumps over the lazy dog
              </TypeExample>
              <Separator />
              <TypeExample size="text-xl" label="Extra Large (1.25rem)" usage="Page subtitles">
                The quick brown fox jumps over the lazy dog
              </TypeExample>
              <Separator />
              <TypeExample size="text-2xl" label="2X Large (1.5rem)" usage="Card titles, h2 headings">
                The quick brown fox jumps over the lazy dog
              </TypeExample>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Font Weights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="font-medium">Medium (500) - font-medium</p>
              <p className="font-semibold">Semibold (600) - font-semibold</p>
              <p className="font-bold">Bold (700) - font-bold</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Spacing Tab */}
        <TabsContent value="spacing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Spacing Scale</CardTitle>
              <CardDescription>Based on Tailwind's 0.25rem unit system</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <SpacingExample size={2} rem="0.5rem" />
              <SpacingExample size={3} rem="0.75rem" />
              <SpacingExample size={4} rem="1rem" />
              <SpacingExample size={6} rem="1.5rem" />
              <SpacingExample size={8} rem="2rem" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Border Radius</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-primary rounded-sm flex items-center justify-center text-white text-xs">
                  sm
                </div>
                <div>
                  <p className="font-medium">rounded-sm</p>
                  <code className="text-xs text-muted-foreground">calc(var(--radius) - 4px) ≈ 4px</code>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-primary rounded-md flex items-center justify-center text-white text-xs">
                  md
                </div>
                <div>
                  <p className="font-medium">rounded-md</p>
                  <code className="text-xs text-muted-foreground">calc(var(--radius) - 2px) ≈ 6px</code>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-primary rounded-lg flex items-center justify-center text-white text-xs">
                  lg
                </div>
                <div>
                  <p className="font-medium">rounded-lg</p>
                  <code className="text-xs text-muted-foreground">var(--radius) = 8px</code>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Components Tab */}
        <TabsContent value="components" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Buttons</CardTitle>
              <CardDescription>Button variants and sizes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="text-sm font-medium mb-3">Variants</h4>
                <div className="flex flex-wrap gap-2">
                  <Button variant="default">Default</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="outline">Outline</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="destructive">Destructive</Button>
                  <Button variant="link">Link</Button>
                </div>
              </div>
              <Separator />
              <div>
                <h4 className="text-sm font-medium mb-3">Sizes</h4>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm">Small</Button>
                  <Button size="default">Default</Button>
                  <Button size="lg">Large</Button>
                  <Button size="icon">🔍</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Badges</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Badge>Default</Badge>
                <Badge variant="secondary">Secondary</Badge>
                <Badge variant="outline">Outline</Badge>
                <Badge variant="destructive">Destructive</Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Inputs</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input placeholder="Default input" />
              <Input placeholder="Disabled input" disabled />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Classes Tab */}
        <TabsContent value="custom" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Custom Athlyn Classes</CardTitle>
              <CardDescription>Custom utility classes defined in src/index.css</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <CustomClassExample
                className="athlyn-card"
                name="athlyn-card"
                description="Standard card styling with hover effects, used throughout the application"
              >
                <p className="text-sm">Card with athlyn-card class applied</p>
              </CustomClassExample>

              <CustomClassExample
                className="athlyn-gradient"
                name="athlyn-gradient"
                description="Athlyn brand gradient (teal to blue)"
              >
                <p className="text-sm text-white font-medium">Gradient background</p>
              </CustomClassExample>

              <CustomClassExample
                className="athlyn-button-primary"
                name="athlyn-button-primary"
                description="Primary button with Athlyn brand styling"
              >
                <button className="athlyn-button-primary">Primary Button</button>
              </CustomClassExample>

              <CustomClassExample
                className="athlyn-input"
                name="athlyn-input"
                description="Custom input field styling"
              >
                <input className="athlyn-input" placeholder="Custom styled input" />
              </CustomClassExample>

              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2 text-sm">Additional Custom Classes:</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li><code>.athlyn-nav-link</code> - Navigation link styling</li>
                  <li><code>.athlyn-table-header</code> - Table header cells</li>
                  <li><code>.athlyn-table-cell</code> - Table body cells</li>
                  <li><code>.athlyn-kpi-card</code> - KPI dashboard cards</li>
                  <li><code>.athlyn-kpi-strip</code> - KPI horizontal strip layout</li>
                  <li><code>.sidebar-link</code> - Sidebar navigation links</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Usage Guidelines</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Prefer semantic color tokens (bg-primary, text-destructive) over direct colors (bg-blue-500)</li>
                <li>Use custom .athlyn-* classes for consistent patterns across pages</li>
                <li>All components should support dark mode with dark: variants</li>
                <li>Use standard spacing scale (gap-4, p-6) for consistency</li>
                <li>Follow the established button and form patterns from shadcn/ui</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New CMS design system (C-CMS-DS) — the primitives new screens use. */}
      <DsShowcase />
    </div>
  );
}

// Helper Components

interface ColorSwatchProps {
  name: string;
  variable?: string;
  value?: string;
  className: string;
  textDark?: boolean;
}

function ColorSwatch({ name, variable, value, className, textDark }: ColorSwatchProps) {
  return (
    <div className="space-y-2">
      <div className={`h-24 rounded-lg ${className} border flex items-center justify-center`}>
        <span className={`text-sm font-medium ${textDark ? 'text-foreground' : 'text-white'}`}>
          {name}
        </span>
      </div>
      <div className="text-xs space-y-0.5">
        {variable && <p className="font-mono text-muted-foreground">{variable}</p>}
        {value && <p className="font-mono text-muted-foreground">{value}</p>}
      </div>
    </div>
  );
}

interface TypeExampleProps {
  size: string;
  label: string;
  usage: string;
  children: React.ReactNode;
}

function TypeExample({ size, label, usage, children }: TypeExampleProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <p className={`${size}`}>{children}</p>
      </div>
      <div className="flex items-center justify-between text-xs">
        <code className="text-muted-foreground">{size}</code>
        <span className="text-muted-foreground">{label}</span>
        <span className="text-muted-foreground italic">{usage}</span>
      </div>
    </div>
  );
}

interface SpacingExampleProps {
  size: number;
  rem: string;
}

function SpacingExample({ size, rem }: SpacingExampleProps) {
  return (
    <div className="flex items-center gap-4">
      <div className={`bg-primary h-8`} style={{ width: `${size * 0.25}rem` }} />
      <div className="text-sm">
        <code className="font-mono">gap-{size}</code>
        <span className="text-muted-foreground ml-2">= {rem}</span>
      </div>
    </div>
  );
}

interface CustomClassExampleProps {
  name: string;
  className: string;
  description: string;
  children: React.ReactNode;
}

function CustomClassExample({ name, className, description, children }: CustomClassExampleProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2">
        <code className="text-sm font-mono">.{name}</code>
        <span className="text-xs text-muted-foreground">{description}</span>
      </div>
      <div className={`p-4 rounded-lg ${className}`}>
        {children}
      </div>
    </div>
  );
}
